import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Download } from "lucide-react";

const STATUS_OPTIONS = ["pending", "in_progress", "outreach_sent", "responses_received", "completed", "closed"];

function WOTable({ orders, api, onUpdate }) {
  const [expandedId, setExpandedId] = useState(null);
  const [editNotes, setEditNotes] = useState({});
  const [editMetrics, setEditMetrics] = useState({});

  const handleUpdate = async (woId, updates) => {
    try {
      await api.put(`/admin/work-orders/${woId}`, updates);
      toast.success("Work order updated");
      onUpdate();
    } catch { toast.error("Update failed"); }
  };

  if (orders.length === 0) {
    return <Card className="border-dashed border-slate-300 bg-slate-50/50"><CardContent className="p-8 text-center"><p className="text-sm text-slate-400">No work orders in this queue</p></CardContent></Card>;
  }

  return (
    <div className="space-y-3">
      {orders.map((wo, idx) => (
        <Card key={wo.id} className="rounded-xl border bg-card animate-slideUp" style={{ animationDelay: `${idx * 0.03}s` }} data-testid={`admin-wo-${wo.id}`}>
          <CardContent className="p-0">
            <button onClick={() => setExpandedId(expandedId === wo.id ? null : wo.id)} className="w-full flex items-center gap-4 p-4 text-left">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-mono text-slate-400">{wo.id?.slice(0, 8)}</span>
                  <StatusBadge status={wo.status} />
                  <Badge className="bg-slate-100 text-slate-600 border-0 text-xs">{wo.org_name}</Badge>
                  {wo.priority === "urgent" && <Badge className="bg-red-100 text-red-700 border-0 text-xs">Urgent</Badge>}
                  {wo.is_demo && <Badge className="bg-amber-100 text-amber-700 border-0 text-xs">Demo</Badge>}
                </div>
                <h3 className="text-sm font-heading text-slate-900">{wo.title}</h3>
              </div>
              <span className="text-xs text-slate-400">{new Date(wo.created_at).toLocaleDateString()}</span>
              {expandedId === wo.id ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
            </button>
            {expandedId === wo.id && (
              <div className="px-4 pb-4 border-t border-slate-100 pt-4 space-y-4 animate-slideDown">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Status</Label>
                    <Select value={wo.status} onValueChange={(val) => handleUpdate(wo.id, { status: val })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Candidates</Label>
                    <div className="flex flex-wrap gap-1">{wo.candidate_pool?.map(id => <Badge key={id} variant="outline" className="font-mono text-xs">{id}</Badge>)}</div>
                  </div>
                </div>
                {!wo.is_demo && (
                  <div className="grid grid-cols-4 gap-3">
                    {[["outreach_sent_count", "Outreach Sent"], ["responses_received_count", "Responses"], ["interested_count", "Interested"], ["consented_count", "Consented"]].map(([key, label]) => (
                      <div key={key}>
                        <Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-1 block">{label}</Label>
                        <Input type="number" value={editMetrics[`${wo.id}_${key}`] ?? wo[key] ?? 0}
                          onChange={e => setEditMetrics(p => ({ ...p, [`${wo.id}_${key}`]: parseInt(e.target.value) || 0 }))}
                          onBlur={() => { const val = editMetrics[`${wo.id}_${key}`]; if (val !== undefined) handleUpdate(wo.id, { [key]: val }); }}
                          className="h-8 text-sm" />
                      </div>
                    ))}
                  </div>
                )}
                <div>
                  <Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Admin Notes</Label>
                  <Textarea value={editNotes[wo.id] ?? wo.admin_notes ?? ""} onChange={e => setEditNotes(p => ({ ...p, [wo.id]: e.target.value }))} className="text-sm" />
                  <Button size="sm" className="mt-2 bg-evergreen-950 hover:bg-evergreen-900 text-white" onClick={() => handleUpdate(wo.id, { admin_notes: editNotes[wo.id] || wo.admin_notes })}>Save Notes</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function WorkOrderQueue() {
  const { api } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadOrders = () => api.get("/admin/work-orders").then(r => setOrders(r.data)).catch(console.error).finally(() => setLoading(false));
  useEffect(() => { loadOrders(); }, [api]); // eslint-disable-line

  const liveOrders = orders.filter(o => !o.is_demo);
  const demoOrders = orders.filter(o => o.is_demo);

  const handleExportCSV = async () => {
    try {
      const res = await api.get("/admin/work-orders/export-csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "work_orders_export.csv"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Work orders exported");
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-wo-queue">
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-2xl font-heading text-slate-900">Work Order Queue</h1>
        <Button variant="outline" size="sm" onClick={handleExportCSV} data-testid="export-wo-csv-btn">
          <Download className="w-4 h-4 mr-1" /> Export CSV
        </Button>
      </div>
      <p className="text-sm text-slate-500 mb-6">Manage all work orders across clients</p>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-white rounded-xl border animate-pulse" />)}</div>
      ) : (
        <Tabs defaultValue="live">
          <TabsList className="mb-6">
            <TabsTrigger value="live" data-testid="tab-live">
              Live <Badge className="bg-lime-100 text-lime-700 border-0 text-xs ml-2">{liveOrders.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="demo" data-testid="tab-demo">
              Demo <Badge className="bg-amber-100 text-amber-700 border-0 text-xs ml-2">{demoOrders.length}</Badge>
            </TabsTrigger>
          </TabsList>
          <TabsContent value="live">
            <WOTable orders={liveOrders} api={api} onUpdate={loadOrders} />
          </TabsContent>
          <TabsContent value="demo">
            <p className="text-xs text-slate-400 italic mb-4">Demo work orders — review only, no action required</p>
            <WOTable orders={demoOrders} api={api} onUpdate={loadOrders} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
